import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Shield, Swords, Zap } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import TalentsPanel from '../components/talents/TalentsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

const QUALITY_COLORS = {
  0: '#9d9d9d', 1: '#9d9d9d', 2: '#1eff00', 3: '#0070dd',
  4: '#a335ee', 5: '#ff8000', 6: '#e6cc80', 7: '#00ccff',
};

const CLASS_AVATAR = {
  'Guerrero':     '/avatars/warrior.jpg',
  'Paladín':      '/avatars/paladin.jpg',
  'Cazador':      '/avatars/hunter.jpg',
  'Pícaro':       '/avatars/rogue.jpg',
  'Sacerdote':    '/avatars/priest.jpg',
  'Caballero de la Muerte': '/avatars/deathknight.jpg',
  'Chamán':       '/avatars/shaman.jpg',
  'Mago':         '/avatars/mage.jpg',
  'Brujo':        '/avatars/warlock.jpg',
  'Druida':       '/avatars/druid.jpg',
};

const SLOTS_LEFT   = [0, 1, 2, 14, 4, 8, 9];
const SLOTS_RIGHT  = [5, 6, 7, 10, 11, 12, 13];
const SLOTS_BOTTOM = [15, 16, 17];

const SLOT_LABEL = {
  0: 'Cabeza', 1: 'Cuello', 2: 'Hombros', 3: 'Camisa', 4: 'Pecho',
  5: 'Cintura', 6: 'Piernas', 7: 'Pies', 8: 'Muñecas', 9: 'Manos',
  10: 'Anillo', 11: 'Anillo', 12: 'Reliquia', 13: 'Reliquia',
  14: 'Espalda', 15: 'Mano ppal.', 16: 'Mano sec.', 17: 'A distancia', 18: 'Tabardo',
};

function ItemSlot({ item, slotId, side = 'center' }) {
  const color = item ? (QUALITY_COLORS[item.quality] ?? '#9d9d9d') : 'rgba(0,229,255,0.12)';
  const iconUrl = item
    ? `https://wow.zamimg.com/images/wow/icons/medium/${item.icon}.jpg`
    : null;

  const iconEl = (
    <div
      className={`charprofile-slot ${item ? 'charprofile-slot--filled' : 'charprofile-slot--empty'}`}
      style={{ borderColor: color }}
    >
      {item ? (
        <a
          href={`https://www.wowhead.com/wotlk/item=${item.entry}`}
          target="_blank"
          rel="noopener noreferrer"
          data-wowhead={`item=${item.entry}&domain=wotlk`}
          className="charprofile-slot-link"
        >
          <img
            src={iconUrl}
            alt={item.name}
            className="charprofile-slot-icon"
            onError={(e) => { e.target.src = 'https://wow.zamimg.com/images/wow/icons/medium/inv_misc_questionmark.jpg'; }}
          />
        </a>
      ) : (
        <span className="charprofile-slot-empty-label">{(SLOT_LABEL[slotId] ?? '')[0]}</span>
      )}
    </div>
  );

  if (side === 'center') {
    return (
      <div className="charprofile-slot-center-wrap">
        {iconEl}
        {item ? (
          <div className="charprofile-slot-center-info">
            <span className="charprofile-slot-item-name charprofile-slot-item-name--center" style={{ color }}>
              {item.name}
            </span>
            <span className="charprofile-slot-ilvl">ilvl {item.item_level}</span>
          </div>
        ) : (
          <span className="charprofile-slot-empty-name charprofile-slot-empty-name--center">
            {SLOT_LABEL[slotId] ?? ''}
          </span>
        )}
      </div>
    );
  }

  // side="left": icon at left edge, text toward center
  // side="right": text toward center, icon at right edge
  const textEl = (
    <div className={`charprofile-slot-text charprofile-slot-text--${side}`}>
      {item ? (
        <>
          <span className="charprofile-slot-item-name" style={{ color }}>{item.name}</span>
          <span className="charprofile-slot-ilvl">ilvl {item.item_level}</span>
        </>
      ) : (
        <span className="charprofile-slot-empty-name">{SLOT_LABEL[slotId] ?? ''}</span>
      )}
    </div>
  );

  return (
    <div className={`charprofile-slot-row charprofile-slot-row--${side}`}>
      {side === 'left' ? <>{iconEl}{textEl}</> : <>{textEl}{iconEl}</>}
    </div>
  );
}

export default function CharacterDetailPage() {
  const { name } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = location.hash === '#talentos' ? 'talentos' : 'equipo';
  const setTab = (t) => navigate({ hash: t === 'talentos' ? '#talentos' : '' }, { replace: true });
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/armory/character/${encodeURIComponent(name)}`)
      .then(({ data }) => setCharacter(data))
      .catch(() => setError('No se pudo cargar el perfil del personaje.'))
      .finally(() => setLoading(false));
  }, [name]);

  const itemBySlot = {};
  if (character?.items) {
    for (const item of character.items) {
      itemBySlot[item.slot] = item;
    }
  }

  const avatarSrc = character ? (CLASS_AVATAR[character.class_name] ?? '/avatars/warrior.jpg') : '/avatars/warrior.jpg';
  const primaryProfs  = character?.professions?.filter(p => p.primary) ?? [];
  const secondaryProfs = character?.professions?.filter(p => !p.primary) ?? [];

  return (
    <PageWrapper scrollable>
      <Navbar />
      <main className="charprofile-main">

        <button className="charprofile-back" onClick={() => navigate('/armeria')}>
          <ArrowLeft size={16} />
          Armería
        </button>

        {loading && <div className="charprofile-loading anim-fade-up">Invocando héroe...</div>}
        {error && <div className="charprofile-error anim-fade-up">{error}</div>}

        {character && !loading && (
          <>
            {/* Header */}
            <div className="charprofile-header anim-fade-up">
              <h1
                className="charprofile-name"
                style={{ color: character.class_color, textShadow: `0 0 30px ${character.class_color}55` }}
              >
                {character.name}
              </h1>
              <p className="charprofile-subtitle">
                Nivel {character.level} · {character.race} · {character.class_name}
                {character.guild && (
                  <> · <span className="charprofile-guild">&lt;{character.guild}&gt;</span></>
                )}
              </p>
              <p className="charprofile-gs">
                <Shield size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Gear Score: <strong>{character.gear_score.toLocaleString()}</strong>
              </p>
            </div>

            <div className="charprofile-tabs anim-fade-up" role="tablist" data-testid="charprofile-tabs">
              {[['equipo', 'Equipo'], ['talentos', 'Talentos']].map(([key, label]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  className={`charprofile-tab ${tab === key ? 'charprofile-tab--active' : ''}`}
                  data-testid={`charprofile-tab-${key}`}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'talentos' && (
              <div className="anim-fade-up" style={{ width: '100%' }}>
                <TalentsPanel backendUrl={BACKEND_URL} name={character.name} />
              </div>
            )}

            {tab === 'equipo' && (<>
            {/* Equipment card */}
            <div className="charprofile-card anim-fade-up" style={{ animationDelay: '0.1s' }}>
              <div className="charprofile-equipment">

                <div className="charprofile-slots-col charprofile-slots-col--left">
                  {SLOTS_LEFT.map(id => (
                    <ItemSlot key={id} item={itemBySlot[id]} slotId={id} side="left" />
                  ))}
                </div>

                <div className="charprofile-avatar-center">
                  <div className="charprofile-avatar-wrap">
                    <img src={avatarSrc} alt={character.class_name} className="charprofile-avatar"
                      loading="lazy" decoding="async"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="charprofile-slots-bottom">
                    {SLOTS_BOTTOM.map(id => (
                      <ItemSlot key={id} item={itemBySlot[id]} slotId={id} side="center" />
                    ))}
                  </div>
                </div>

                <div className="charprofile-slots-col charprofile-slots-col--right">
                  {SLOTS_RIGHT.map(id => (
                    <ItemSlot key={id} item={itemBySlot[id]} slotId={id} side="right" />
                  ))}
                </div>

              </div>
            </div>

            {/* Info panel: PvP + Profesiones */}
            <div className="charprofile-info-panel anim-fade-up" style={{ animationDelay: '0.2s' }}>

              {/* Columna izquierda: PvP y Arena */}
              <div className="charprofile-info-col">
                <div className="charprofile-info-section">
                  <h3 className="charprofile-info-title">
                    <Swords size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Jugador contra Jugador
                  </h3>
                  <div className="charprofile-stat-row">
                    <span className="charprofile-stat-label">Bajas totales</span>
                    <span className="charprofile-stat-value">{(character.total_kills ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="charprofile-stat-row">
                    <span className="charprofile-stat-label">Bajas hoy</span>
                    <span className="charprofile-stat-value">{character.today_kills ?? 0}</span>
                  </div>
                </div>

                {character.arena_teams?.length > 0 && (
                  <div className="charprofile-info-section">
                    <h3 className="charprofile-info-title">Equipos de Arena</h3>
                    {character.arena_teams.map((team) => (
                      <div key={`${team.bracket}-${team.name}`} className="charprofile-arena-row">
                        <span className="charprofile-arena-bracket">{team.bracket}</span>
                        <span className="charprofile-arena-name">{team.name}</span>
                        <span className="charprofile-arena-rating">{team.rating}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Columna derecha: Profesiones */}
              <div className="charprofile-info-col">
                {primaryProfs.length > 0 && (
                  <div className="charprofile-info-section">
                    <h3 className="charprofile-info-title">
                      <Zap size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Profesiones
                    </h3>
                    {primaryProfs.map((p) => (
                      <div key={p.name} className="charprofile-prof-row">
                        <span className="charprofile-prof-name">{p.name}</span>
                        <div className="charprofile-prof-bar-wrap">
                          <div className="charprofile-prof-bar" style={{ width: `${Math.min(100, (p.value / p.max) * 100)}%` }} />
                        </div>
                        <span className="charprofile-prof-value">{p.value} / {p.max}</span>
                      </div>
                    ))}
                  </div>
                )}

                {secondaryProfs.length > 0 && (
                  <div className="charprofile-info-section">
                    <h3 className="charprofile-info-title">Habilidades secundarias</h3>
                    {secondaryProfs.map((p) => (
                      <div key={p.name} className="charprofile-prof-row">
                        <span className="charprofile-prof-name">{p.name}</span>
                        <div className="charprofile-prof-bar-wrap">
                          <div className="charprofile-prof-bar" style={{ width: `${Math.min(100, (p.value / p.max) * 100)}%` }} />
                        </div>
                        <span className="charprofile-prof-value">{p.value} / {p.max}</span>
                      </div>
                    ))}
                  </div>
                )}

                {primaryProfs.length === 0 && secondaryProfs.length === 0 && (
                  <div className="charprofile-info-section">
                    <h3 className="charprofile-info-title">
                      <Zap size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Profesiones
                    </h3>
                    <p className="charprofile-info-empty">Sin profesiones registradas</p>
                  </div>
                )}
              </div>

            </div>
            </>)}
          </>
        )}

      </main>
      <Footer />
    </PageWrapper>
  );
}
